import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { collection, doc, getDoc, getDocs, query, where } from "firebase/firestore";
import { db } from "../firebase";
import { Logo } from "../components/Logo";
import MarketingNav from "../components/MarketingNav";
import { NicheBadge, getNicheMeta } from "../components/niche";
import { fetchFeedDrops, isOpenFeedDrop } from "../services/dropsFeedService";
import { formatDropPrice } from "../utils/dropPricing";
import HeroCounterLock from "./HeroCounterLock";
import "./LandingPage.css";

function scrollTo(id) {
  document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
}

const ICON_STROKE = {
  width: 18,
  height: 18,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.8,
  strokeLinecap: "round",
  strokeLinejoin: "round",
  "aria-hidden": true,
};

function IconLock() {
  return (
    <svg {...ICON_STROKE}>
      <rect x="4" y="10" width="16" height="10" rx="2" />
      <path d="M8 10V7a4 4 0 018 0v3" />
    </svg>
  );
}

function IconZap() {
  return (
    <svg {...ICON_STROKE}>
      <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
    </svg>
  );
}

function IconSparkle() {
  return (
    <svg {...ICON_STROKE}>
      <path d="M12 2l1.6 5.2L19 9l-5.4 1.8L12 16l-1.6-5.2L5 9l5.4-1.8L12 2z" />
      <path d="M19 15l.7 2.3L22 18l-2.3.7L19 21l-.7-2.3L16 18l2.3-.7L19 15z" />
    </svg>
  );
}

const MOSAIC = [
  { src: "https://images.unsplash.com/photo-1486262715619-67b85e0b08d3?w=600&q=85", label: "Auto & Trades", tall: true },
  { src: "https://images.unsplash.com/photo-1616394584738-fc6e612e71b9?w=600&q=85", label: "Wellness" },
  { src: "https://images.unsplash.com/photo-1534438327276-14e5300c3a48?w=600&q=85", label: "Fitness" },
  { src: "https://images.unsplash.com/photo-1490645935967-10de6ba17061?w=600&q=85", label: "Food" },
  { src: "https://images.unsplash.com/photo-1522337360788-8b13dee7a37e?w=600&q=85", label: "Beauty" },
];

const SOCIAL_AVATARS = [
  "https://images.unsplash.com/photo-1531746020798-e6953c6e8e04?w=80&q=80",
  "https://images.unsplash.com/photo-1564564321837-a57b7070ac4f?w=80&q=80",
  "https://images.unsplash.com/photo-1494790108755-2616b612b47c?w=80&q=80",
  "https://images.unsplash.com/photo-1517841905240-472988babdf9?w=80&q=80",
  "https://images.unsplash.com/photo-1526413232644-8a40f03cc03b?w=80&q=80",
];

const FLOAT_PILLS = [
  {
    avatar: "https://images.unsplash.com/photo-1531746020798-e6953c6e8e04?w=80&q=80",
    name: "Anthea Rose",
    title: "Morning Ritual Vol. 4",
    views: "63/100",
    hot: false,
  },
  {
    avatar: "https://images.unsplash.com/photo-1564564321837-a57b7070ac4f?w=80&q=80",
    name: "Mike Reeves",
    title: "Engine Swap Vol. 2",
    views: "87/100",
    hot: true,
  },
  {
    avatar: "https://images.unsplash.com/photo-1494790108755-2616b612b47c?w=80&q=80",
    name: "Jessica Lee",
    title: "Skin Ritual Walkthrough",
    views: "91/100",
    hot: true,
  },
  {
    avatar: "https://images.unsplash.com/photo-1517841905240-472988babdf9?w=80&q=80",
    name: "Chef Marie",
    title: "Food & Cooking",
    views: "67/100",
    hot: false,
  },
  {
    avatar: "https://images.unsplash.com/photo-1526413232644-8a40f03cc03b?w=80&q=80",
    name: "Kai",
    title: "Fitness & Sport",
    views: "52/100",
    hot: false,
  },
];

/** Curated examples — used only when fewer than 3 real open drops exist. */
const FALLBACK_LIVE_DROPS = [
  {
    id: "demo-mike",
    avatar: "https://images.unsplash.com/photo-1564564321837-a57b7070ac4f?w=80&q=80",
    name: "Mike Reeves",
    niche: "Auto",
    title: "Engine Swap Vol. 2",
    views: 87,
    max: 100,
    price: "$7.99",
  },
  {
    id: "demo-jessica",
    avatar: "https://images.unsplash.com/photo-1494790108755-2616b612b47c?w=80&q=80",
    name: "Jessica Lee",
    niche: "Beauty",
    title: "Skin Ritual Walkthrough",
    views: 91,
    max: 100,
    price: "$4.99",
  },
  {
    id: "demo-anthea",
    avatar: "https://images.unsplash.com/photo-1531746020798-e6953c6e8e04?w=80&q=80",
    name: "Anthea Rose",
    niche: "Wellness",
    title: "Morning Ritual Vol. 4",
    views: 63,
    max: 100,
    price: "Free",
  },
];

/** Niche keys map to canonical --niche-*-fg CSS tokens via getNicheMeta. */
const NICHES = [
  {
    key: "auto",
    creator: "@mikereeves",
    views: "87 / 100",
    img: "https://images.unsplash.com/photo-1486262715619-67b85e0b08d3?w=700&q=85",
    quote: "My shop content hits different when only 100 people can see it.",
  },
  {
    key: "food",
    creator: "@chefmarie",
    views: "67 / 100",
    img: "https://images.unsplash.com/photo-1490645935967-10de6ba17061?w=700&q=85",
    quote: "Recipe drops sell out before I finish plating. That energy is unreal.",
  },
  {
    key: "fitness",
    creator: "@movewithkai",
    views: "52 / 100",
    img: "https://images.unsplash.com/photo-1534438327276-14e5300c3a48?w=700&q=85",
    quote: "100 spots means my core crew actually shows up every single time.",
  },
  {
    key: "wellness",
    creator: "@anthearose",
    views: "63 / 100",
    img: "https://images.unsplash.com/photo-1616394584738-fc6e612e71b9?w=700&q=85",
    quote: "Scarcity changed how my community treats every ritual I share.",
  },
  {
    key: "beauty",
    creator: "@jessicalee",
    views: "91 / 100",
    img: "https://images.unsplash.com/photo-1522337360788-8b13dee7a37e?w=700&q=85",
    quote: "When the window is real, people stop scrolling and start buying.",
  },
];

const STEPS = [
  {
    num: "01",
    photo: "https://images.unsplash.com/photo-1611532736597-de2d4265fba3?w=700&q=85",
    title: "You post a drop.",
    lead: "Upload anything — video, audio, photos, writing. Set a price.",
    bold: "It goes live immediately.",
    tail: "No review queue. No algorithm.",
    hand: "any niche · any format · 🍁 Canadian creators",
  },
  {
    num: "02",
    photo: "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=700&q=85",
    title: "100 people get in.",
    lead: "The first 100 viewers claim their spot.",
    bold: "Their name is in your drop's history permanently.",
    tail: "Being first actually means something.",
    hand: "real urgency. real community.",
  },
  {
    num: "03",
    photo: "https://images.unsplash.com/photo-1499750310107-5fef28a66643?w=700&q=85",
    title: "The window closes.",
    lead: "At 100 first views, the first-view window closes permanently.",
    bold: "Subscribers keep access forever — but the moment of being first to see it is gone.",
    tail: "Every drop becomes an event.",
    hand: "not a trick. that's how it works.",
  },
];

const CREATOR_FEATURES = [
  {
    icon: "lock",
    bold: "10% platform fee, locked forever",
    tail: "for founding creators. Not \u201cfor now.\u201d Locked.",
  },
  {
    icon: "maple",
    bold: "CAD payouts direct to your bank",
    tail: "via Stripe. No conversion fees.",
  },
  {
    icon: "zap",
    bold: "Scarcity markets itself.",
    tail: "When a drop hits 80/100, fans urgently share it. Your window stays open until 100 people find it — no time limit.",
  },
  {
    icon: "sparkle",
    bold: "Your content, your price.",
    tail: "Tiers, individual drops, or both.",
  },
];

function FeatureIcon({ type }) {
  if (type === "maple") return <span aria-hidden="true">🍁</span>;
  if (type === "lock") return <IconLock />;
  if (type === "zap") return <IconZap />;
  if (type === "sparkle") return <IconSparkle />;
  return null;
}

const CREATOR_STATS = [
  { value: "10%", label: "Platform fee — locked for founding creators", hand: "forever. we mean it." },
  { value: "$0", label: "To set up and start posting", hand: "no setup fees. no monthly cost." },
  { value: "CAD", label: "All payouts in Canadian dollars", hand: "straight to your bank account." },
];

const VOICES = [
  {
    quote: "Finally a platform where being early actually means something. My fans show up because they know the window is real.",
    avatar: "https://images.unsplash.com/photo-1531746020798-e6953c6e8e04?w=80&q=80",
    name: "Anthea Rose",
    role: "Wellness creator · 156K",
  },
  {
    quote: "I don't need a million followers. I need 100 people who actually care. That's what this is built for.",
    avatar: "https://images.unsplash.com/photo-1494790108755-2616b612b47c?w=80&q=80",
    name: "Jade M.",
    role: "Beauty creator · Toronto",
  },
  {
    quote: "CAD payouts without the USD conversion hit? That's the whole reason I applied on day one.",
    avatar: "https://images.unsplash.com/photo-1564564321837-a57b7070ac4f?w=80&q=80",
    name: "Mike Reeves",
    role: "Auto & Trades · Parkhill, ON",
  },
];

function isPermissionError(err) {
  return err?.code === "permission-denied" || err?.code === "unauthenticated";
}

function useCreatorCount() {
  const [count, setCount] = useState(null);

  useEffect(() => {
    let cancelled = false;
    getDocs(query(collection(db, "users"), where("isCreator", "==", true)))
      .then((snap) => {
        if (!cancelled) setCount(snap.size);
      })
      .catch((err) => {
        // Marketing page — keep the static fallback; never log expected logged-out denials.
        if (!isPermissionError(err)) {
          console.error("Landing creator count error:", err);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return count;
}

function useLiveLandingDrops() {
  const [drops, setDrops] = useState(FALLBACK_LIVE_DROPS);
  const [isLive, setIsLive] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const fetched = await fetchFeedDrops(40);
        const open = fetched
          .filter(isOpenFeedDrop)
          .sort((a, b) => {
            const byViews = (Number(b.viewCount) || 0) - (Number(a.viewCount) || 0);
            if (byViews !== 0) return byViews;
            return (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0);
          })
          .slice(0, 3);

        if (open.length < 3) {
          if (!cancelled) {
            setDrops(FALLBACK_LIVE_DROPS);
            setIsLive(false);
          }
          return;
        }

        const creatorIds = [...new Set(open.map((d) => d.creatorId).filter(Boolean))];
        const pairs = await Promise.all(
          creatorIds.map(async (id) => {
            try {
              const snap = await getDoc(doc(db, "users", id));
              return [id, snap.exists() ? snap.data() : {}];
            } catch {
              return [id, {}];
            }
          })
        );
        const creators = Object.fromEntries(pairs);

        const mapped = open.map((d) => {
          const creator = creators[d.creatorId] || {};
          return {
            id: d.id,
            avatar: creator.avatarUrl || creator.avatar || d.thumbnailUrl || "",
            name: creator.displayName || creator.username || "Creator",
            niche: d.niche || creator.niche || "Independent",
            title: d.title || "Untitled drop",
            views: Number(d.viewCount) || 0,
            max: Number(d.maxViews) || 100,
            price: formatDropPrice(d),
          };
        });

        if (!cancelled) {
          setDrops(mapped);
          setIsLive(true);
        }
      } catch (err) {
        if (!isPermissionError(err)) {
          console.error("Landing live drops error:", err);
        }
        if (!cancelled) {
          setDrops(FALLBACK_LIVE_DROPS);
          setIsLive(false);
        }
      }
    };

    load();
    return () => { cancelled = true; };
  }, []);

  return { drops, isLive };
}

export default function LandingPage() {
  const location = useLocation();
  const creatorCount = useCreatorCount();
  const creatorNumber = creatorCount === null ? "38" : creatorCount;
  const { drops: liveDrops, isLive: liveDropsAreReal } = useLiveLandingDrops();
  const counterDrop = liveDrops[2] || liveDrops[0] || FALLBACK_LIVE_DROPS[2];

  useEffect(() => {
    document.documentElement.classList.add("lp-scroll");
    return () => document.documentElement.classList.remove("lp-scroll");
  }, []);

  useEffect(() => {
    const id = location.hash?.replace("#", "");
    if (!id) return undefined;
    const t = window.setTimeout(() => scrollTo(id), 80);
    return () => window.clearTimeout(t);
  }, [location.hash, location.pathname]);

  return (
    <div className="lp-page">
      <MarketingNav />

      {/* ── 2. HERO ── */}
      <section className="lp-hero">
        <div className="lp-hero-grid">
          <div className="lp-hero-copy">
            <div className="lp-atmosphere">
              <div className="lp-blob lp-blob--gold-light" />
              <div className="lp-blob lp-blob--gold" />
              <div className="lp-blob lp-blob--green" />
              <div className="lp-blob lp-blob--rust" />
            </div>
            <svg width="0" height="0" aria-hidden="true">
              <filter id="lp-noise"><feTurbulence type="fractalNoise" baseFrequency="0.85" numOctaves="2" /></filter>
            </svg>
            <div className="lp-grain" style={{ background: "transparent", filter: "url(#lp-noise)" }} />
            {/* Desktop-only floats (left column). Mobile floats live inside the mosaic band. */}
            <div className="lp-hero-floats lp-hero-floats--desktop" aria-hidden="true">
              {FLOAT_PILLS.map((pill, i) => (
                <div key={`desk-${i}-${pill.name}`} className={`lp-float-pill lp-float-pill--${i + 1}`}>
                  <img src={pill.avatar} alt="" className="lp-float-pill-avatar" />
                  <div className="lp-float-pill-text">
                    <strong>{pill.name}</strong>
                    <span>{pill.title}</span>
                  </div>
                  <span className={`lp-float-pill-views${pill.hot ? " hot" : ""}`}>
                    {pill.views}{pill.hot ? " ⚡" : ""}
                  </span>
                </div>
              ))}
            </div>

            <div className="lp-eyebrow-pill">
              <span className="lp-live-dot" />
              <span>
                <strong>{creatorNumber} Canadian creators</strong>
                {" · fans worldwide welcome"}
                {liveDropsAreReal ? ` · ${liveDrops.length} drops open` : ""}
              </span>
            </div>

            <h1 className="lp-hero-title">
              Your drop. 100 people. <em>Then it&apos;s gone.</em>
            </h1>

            <span className="lp-hero-note">yes, actually gone. forever.</span>

            <div className="lp-canada-badge">
              <span className="lp-canada-badge-emoji" aria-hidden="true">🍁</span>
              <div className="lp-canada-badge-text">
                <strong>Canadian creators only · Fans worldwide welcome</strong>
                <span>All creators are Canadian. Anyone in the world can subscribe and watch.</span>
              </div>
            </div>

            <p className="lp-hero-body">
              Whether you fix engines, cook food, train bodies, or just have something
              worth saying — <strong>your first 100 viewers get in.</strong> Then the
              window closes. That&apos;s not a gimmick. That&apos;s the whole point.
              <span className="lp-hero-body-sub">
                Subscribers keep access to every drop forever — the window just makes being first mean something.
              </span>
            </p>

            <HeroCounterLock drop={counterDrop} />

            <div className="lp-hero-ctas">
              <button type="button" className="lp-btn-dark lg" onClick={() => scrollTo("live-drops")}>
                Explore Drops — Free
              </button>
              <Link to="/waitlist/creator" className="lp-btn-gold lg">
                Post Your First Drop
              </Link>
            </div>
            <p className="lp-hero-note-small">No credit card · 🍁 Canadian creators · Fans worldwide welcome</p>

            <div className="lp-social-proof">
              <div className="lp-avatar-stack">
                {SOCIAL_AVATARS.map((src) => (
                  <img key={src} src={src} alt="" />
                ))}
              </div>
              <p>
                <span className="lp-social-stars">★★★★★</span>
                <strong>2,841 fans</strong> · wellness, auto, food, fitness + more
              </p>
            </div>
          </div>

          <div className="lp-hero-visual">
            <div className="lp-hero-floats lp-hero-floats--mobile" aria-hidden="true">
              {FLOAT_PILLS.map((pill, i) => (
                <div key={`mob-${i}-${pill.name}`} className={`lp-float-pill lp-float-pill--${i + 1}`}>
                  <img src={pill.avatar} alt="" className="lp-float-pill-avatar" />
                  <div className="lp-float-pill-text">
                    <strong>{pill.name}</strong>
                    <span>{pill.title}</span>
                  </div>
                  <span className={`lp-float-pill-views${pill.hot ? " hot" : ""}`}>
                    {pill.views}{pill.hot ? " ⚡" : ""}
                  </span>
                </div>
              ))}
            </div>
            <div className="lp-mosaic">
              {MOSAIC.map((item) => (
                <div
                  key={item.label}
                  className={`lp-mosaic-item${item.tall ? " span-tall" : ""}`}
                >
                  <img src={item.src} alt={item.label} className="lp-photo" />
                  <span className="lp-mosaic-label">{item.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── 3. LIVE DROPS ── */}
      <section className="lp-section lp-section-white lp-section--live-drops" id="live-drops">
        <div className="lp-container">
          <p className="lp-section-label">
            {liveDropsAreReal ? (
              <>
                <span className="lp-blink-dot" />
                Open right now — not for long
              </>
            ) : (
              "Sample drops — what open windows look like"
            )}
          </p>

          <div className="lp-drops-grid">
            {liveDrops.map((drop) => {
              const remaining = Math.max(0, drop.max - drop.views);
              const pct = Math.min(100, (drop.views / drop.max) * 100);
              return (
                <article key={drop.id || drop.title} className="lp-drop-card">
                  <div className="lp-drop-header">
                    <img src={drop.avatar} alt={drop.name} className="lp-photo" />
                    <div className="lp-drop-header-text">
                      <strong>{drop.name}</strong>
                      <NicheBadge niche={drop.niche} className="lp-niche-badge" />
                    </div>
                  </div>
                  <h3 className="lp-drop-title">{drop.title}</h3>
                  <div className="lp-progress-wrap">
                    <div className="lp-progress-bar">
                      <div className="lp-progress-fill" style={{ width: `${pct}%` }} />
                    </div>
                    <div className="lp-progress-meta">
                      <span>{drop.views} / {drop.max} views</span>
                      <span className="lp-views-pill">{remaining} left</span>
                    </div>
                  </div>
                  <div className="lp-drop-price">{drop.price}</div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── 4. NICHES ── */}
      <section className="lp-section lp-section-cream" id="discover">
        <div className="lp-container">
          <p className="lp-section-label">Every niche. One platform.</p>
          <h2 className="lp-section-title">Your content <em>fits here.</em></h2>
          <span className="lp-sec-hand">yes, even that thing you make</span>
          <p className="lp-sec-sub">
            The 100-viewer window creates urgency for any content. A mechanic dropping
            an unedited engine swap gets the same closing-soon energy as a wellness
            creator's morning ritual.
          </p>
          <div className="lp-inline-badge">
            <span aria-hidden="true">🍁</span>
            <span>All creators on 100ViewsOnly are based in Canada — fans from anywhere can subscribe and watch.</span>
          </div>

          <div className="lp-niches-grid">
            {NICHES.map((niche) => {
              const meta = getNicheMeta(niche.key);
              return (
                <article key={meta.key} className="lp-niche-tile">
                  <div className="lp-niche-photo">
                    <div
                      className="lp-niche-accent"
                      style={{
                        background: `linear-gradient(90deg, var(--niche-${meta.key}-fg) 0%, var(--niche-${meta.key}-fg) 42%, transparent 100%)`,
                      }}
                    />
                    <img src={niche.img} alt={meta.label} className="lp-photo" loading="lazy" />
                    <div className="lp-niche-shade" />
                    <div className="lp-niche-info">
                      <strong>{meta.label}</strong>
                      <span>{niche.creator} · {niche.views}</span>
                    </div>
                  </div>
                  <p className="lp-niche-quote">&ldquo;{niche.quote}&rdquo;</p>
                </article>
              );
            })}

            <div className="lp-niche-apply">
              <span className="lp-you-hand">← and whatever you make</span>
              <div className="lp-you-icon" aria-hidden="true"><IconSparkle /></div>
              <h3>Your niche goes here.</h3>
              <p>If you have something worth seeing, there are 100 people who need to be first to see it.</p>
              <Link to="/waitlist/creator" className="lp-btn-gold">
                Apply as a Creator →
              </Link>
              <span className="lp-you-note">🍁 Canadian creators · Waitlist open now</span>
            </div>
          </div>
        </div>
      </section>

      {/* ── 5. HOW IT WORKS ── */}
      <section className="lp-section lp-section-white" id="how">
        <div className="lp-container">
          <p className="lp-section-label">How it works</p>
          <h2 className="lp-section-title">Three steps. <em>One rule.</em></h2>

          <div className="lp-steps-grid">
            {STEPS.map((step) => (
              <article key={step.num} className="lp-step">
                <div className="lp-step-num">{step.num}</div>
                <img className="lp-step-photo lp-photo" src={step.photo} alt="" loading="lazy" />
                <h3>{step.title}</h3>
                <p>
                  {step.lead} <strong>{step.bold}</strong> {step.tail}
                </p>
                <span className="lp-hw-hand">{step.hand}</span>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ── 6. FOR CREATORS ── */}
      <section className="lp-creators" id="creators">
        <div className="lp-container">
          <div className="lp-creators-grid">
            <div>
              <p className="lp-section-label lp-section-label--dark">For creators</p>
              <h2>
                A platform that actually <em>pays you.</em>
              </h2>
              <span className="lp-fc-hand">not eventually. from drop one.</span>
              <p className="lp-fc-body">
                No fighting an algorithm for reach. <strong>Post a drop, 100 people find
                it, you get paid.</strong> The scarcity does the marketing for you — fans
                urgently share drops that are closing.
              </p>

              <div className="lp-canada-badge lp-canada-badge--dark">
                <span className="lp-canada-badge-emoji" aria-hidden="true">🍁</span>
                <div className="lp-canada-badge-text">
                  <strong>Waitlist open for creators based in Canada</strong>
                  <span>Post and earn in CAD from a Canadian base — your fans can subscribe and watch from anywhere in the world.</span>
                </div>
              </div>

              <ul className="lp-feature-list">
                {CREATOR_FEATURES.map((f) => (
                  <li key={f.bold}>
                    <span className="lp-feature-icon">
                      <FeatureIcon type={f.icon} />
                    </span>
                    <div>
                      <strong>{f.bold}</strong> {f.tail}
                    </div>
                  </li>
                ))}
              </ul>

              <Link to="/waitlist/creator" className="lp-btn-gold lg">
                Apply as a Founding Creator
              </Link>
            </div>

            <div className="lp-stat-cards">
              {CREATOR_STATS.map((stat) => (
                <div key={stat.value} className="lp-stat-card">
                  <strong>{stat.value}</strong>
                  <span>{stat.label}</span>
                  <span className="lp-fcs-hand">{stat.hand}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── 7. VOICES ── */}
      <section className="lp-section lp-section-cream">
        <div className="lp-container">
          <p className="lp-section-label">From our community</p>
          <h2 className="lp-section-title">What people actually <em>said.</em></h2>

          <div className="lp-voices-grid">
            {VOICES.map((v) => (
              <article key={v.name} className="lp-voice-card">
                <blockquote>{v.quote}</blockquote>
                <div className="lp-voice-author">
                  <img src={v.avatar} alt={v.name} className="lp-photo" />
                  <div>
                    <strong>{v.name}</strong>
                    <span>{v.role}</span>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ── 8. CTA / WAITLIST ── */}
      <section className="lp-cta-section" id="waitlist">
        <div className="lp-container">
          <h2>Don&rsquo;t miss<br />the <em>next window.</em></h2>
          <span className="lp-cta-hand">seriously — some of these close today</span>
          <p>
            Drops close at 100 views. Some close in hours. Join free as a fan — or
            apply as a creator and post your first drop today.
          </p>
          <div className="lp-cta-buttons">
            <Link to="/waitlist/fan" className="lp-btn-dark lg">
              Explore Drops — Free
            </Link>
            <Link to="/waitlist/creator" className="lp-btn-gold lg">
              Apply as a Creator
            </Link>
          </div>
          <p className="lp-cta-note">Browse and discover drops for free — no account needed · 🍁 Canadian creators only · Fans worldwide welcome · CASL compliant</p>
        </div>
      </section>

      {/* ── 9. FOOTER ── */}
      <footer className="lp-footer">
        <div className="lp-container">
          <div className="lp-footer-inner">
            <Logo size="sm" dark />

            <nav className="lp-footer-links">
              <a href="#live-drops">Discover</a>
              <a href="#creators">For Creators</a>
              <a href="#how">How it works</a>
              <Link to="/privacy">Privacy</Link>
              <Link to="/terms">Terms</Link>
            </nav>

            <p className="lp-footer-meta">🍁 Built in Canada · Est. 2026</p>
          </div>
        </div>
      </footer>
    </div>
  );
}